/**
 * The Outline (R4): the form as a tree — Pages ▸ Sections ▸ Cards ▸ Widgets ▸
 * Fields — built from the real flat-section model (a section with `pageBreak`
 * starts a page) and the field spine the canvas keeps. Clicking a node selects
 * it on the canvas; the parent switches page and scrolls.
 *
 * Tags: ƒ on anything a rule reads (accent: it triggers rules) or writes
 * (logic colour: a rule changes it), and p1/p2 on a field the document places
 * on a page. R5 reuses this component for the Logic and Document sidebars.
 */
import React, { useMemo, useState } from 'react';
import type { FormRule, FormWidget, WidgetNode } from '../../shared/types';
import { cardTarget, sectionTarget, widgetTarget } from '../../form-engine/rules';
import { BField, BSection, contOf, inCont, isStructural, nodesOf, pagesOf } from './builderModel';
import { paletteEntryFor } from './builderPalette';

export type BuilderSel =
  | { t: 'field'; key: string }
  | { t: 'sec'; id: string }
  | { t: 'card'; sec: string; id: string }
  | { t: 'wnode'; sec: string; scope: string }
  | { t: 'form' }
  | null;

/** What a rule reads and what it writes, by key (fields) or target (`sec:`, `card:`, `wnode:`). */
export function ruleTouches(rules: FormRule[]): { reads: Set<string>; writes: Set<string> } {
  const d = ruleTagDetail(rules);
  return { reads: new Set([...d.keys()].filter((k) => d.get(k)!.reads > 0)), writes: new Set([...d.keys()].filter((k) => d.get(k)!.writes.length > 0)) };
}

/** The word the logic-mode tag shows for an action that changes a field. */
const WRITE_WORD: Record<string, string> = {
  show: 'show', hide: 'hide', require: 'require', optional: 'optional', lock: 'lock', unlock: 'unlock',
  prefill: 'prefill', calc: 'calculate',
};

/**
 * Per key or target: how many rules READ it (a condition, a prefill source, a
 * calc input) and the verbs of the actions that WRITE it, in rule order. The
 * Outline's logic mode (R5) says "ƒ triggers 3" and "ƒ show" from this; the
 * Fields tab's compact ƒ tags read the same map.
 */
export function ruleTagDetail(rules: FormRule[]): Map<string, { reads: number; writes: string[] }> {
  const m = new Map<string, { reads: number; writes: string[] }>();
  const at = (k: string) => { const i = k.indexOf('.'); const key = i < 0 ? k : k.slice(0, i); let e = m.get(key); if (!e) { e = { reads: 0, writes: [] }; m.set(key, e); } return e; };
  for (const r of rules) {
    const read = new Set<string>();
    (r.when || []).forEach((c) => { if (c.field) read.add(c.field); });
    for (const a of r.actions || []) {
      if (a.type === 'prefill') (a.map || []).forEach((m2) => { if (m2.from) read.add(m2.from); if (m2.to) at(m2.to).writes.push(WRITE_WORD.prefill); });
      else if (a.type === 'calc') {
        if (a.target) at(a.target).writes.push(WRITE_WORD.calc);
        if (a.calc.type === 'sum') read.add(a.calc.field);
        if (a.calc.type === 'math') { read.add(a.calc.a); read.add(a.calc.b); }
        if (a.calc.type === 'join') a.calc.fields.forEach((k) => read.add(k));
      } else if (a.type === 'goto') { /* a page, not a field */ }
      else (a.targets || []).forEach((t) => { if (t) at(t).writes.push(WRITE_WORD[a.type] || a.type); });
    }
    // One rule reading a key twice is one trigger, not two.
    for (const k of read) at(k).reads += 1;
  }
  return m;
}

export interface OutlineProps {
  sections: BSection[];
  fields: BField[];
  widgets: FormWidget[];
  rules: FormRule[];
  /** the document's placements, for the p{n} tags */
  placements?: { fieldKey: string; page: number }[];
  sel: BuilderSel;
  onSelect: (sel: BuilderSel) => void;
  /** The foot's two actions. Both absent (R5's Logic and Document) and the foot is not rendered. */
  onAddField?: () => void;
  onAddPage?: () => void;
  /**
   * How the ƒ tags read (R5). `compact` is the Fields tab's one glyph;
   * `logic` spells it out — "ƒ triggers 3" on a field rules read, "ƒ show" /
   * "ƒ require" / "ƒ calculate" on one they change; `document` shows only the
   * p{n} tags.
   */
  tagMode?: 'compact' | 'logic' | 'document';
  /** The note beside the title: "{n} fields" unless a mode has something else to say. */
  countLabel?: string;
  /**
   * Make a node draggable (R5's Document tab): the payload for the drag, or
   * null for a node that cannot be dragged. Set as `text/plain`, the way the
   * placement canvas reads its drops.
   */
  dragData?: (sel: BuilderSel) => string | null;
  /**
   * The answers of the forms this one continues (the Document tab), listed
   * under the form's own pages: one group per heading, each answer a field
   * node keyed as the document names it — `parent.companyName`. No field of
   * the form's own has a dotted key, so the selection is still a `field`.
   */
  earlier?: { label: string; items: { key: string; label: string }[] }[];
}

export default function BuilderOutline({
  sections, fields, widgets, rules, placements, sel, onSelect, onAddField, onAddPage, tagMode = 'compact', countLabel, dragData, earlier,
}: OutlineProps) {
  const [shut, setShut] = useState<Set<string>>(() => new Set());
  const widgetSecIds = useMemo(() => new Set(sections.filter((s) => s.type === 'locations' || s.type === 'widget').map((s) => s.id)), [sections]);
  const pages = useMemo(() => pagesOf(sections), [sections]);
  const detail = useMemo(() => ruleTagDetail(rules), [rules]);
  const placed = useMemo(() => {
    const m = new Map<string, number[]>();
    const add = (k: string, page: number) => {
      const list = m.get(k) || [];
      if (!list.includes(page)) list.push(page);
      m.set(k, list);
    };
    for (const p of placements || []) {
      const k = p.fieldKey.split('.')[0];
      add(k, p.page);
      // An earlier form's answer is tagged on its own row, by its whole key.
      if (k !== p.fieldKey) add(p.fieldKey, p.page);
    }
    return m;
  }, [placements]);
  const nFields = fields.filter((f) => !isStructural(f)).length;

  const widgetFor = (sec: BSection): FormWidget | undefined =>
    sec.type === 'locations' ? (widgets.find((w) => w.id === 'locations') || widgets.find((w) => w.builtin))
      : sec.type === 'widget' ? widgets.find((w) => w.id === sec.widgetId) : undefined;

  const tags = (readKey: string, writeKey = readKey, key?: string) => {
    const out: React.ReactNode[] = [];
    const reads = detail.get(readKey)?.reads || 0;
    const writes = detail.get(writeKey)?.writes || [];
    if (tagMode !== 'document') {
      if (reads > 0) {
        out.push(
          <span key="r" className="fb-ol-tag reads" title={`Triggers ${reads} rule${reads === 1 ? '' : 's'}`}>
            ƒ{tagMode === 'logic' ? ` triggers ${reads}` : ''}
          </span>
        );
      } else if (writes.length) {
        // The first verb names the tag; the title lists them all.
        const words = [...new Set(writes)];
        out.push(
          <span key="w" className="fb-ol-tag writes" title={`Changed by a rule: ${words.join(', ')}`}>
            ƒ{tagMode === 'logic' ? ` ${words[0]}` : ''}
          </span>
        );
      }
    }
    for (const p of placed.get(key || '') || []) out.push(<span key={`p${p}`} className="fb-ol-tag page" title={`Placed on page ${p} of the document`}>p{p}</span>);
    return out;
  };

  const same = (a: BuilderSel, b: BuilderSel) => JSON.stringify(a) === JSON.stringify(b);

  const node = (s: BuilderSel, depth: number, glyph: string, label: string, extra?: React.ReactNode, cls = '') => {
    const payload = dragData ? dragData(s) : null;
    return (
      <button
        type="button" role="treeitem" aria-selected={same(sel, s)}
        className={`fb-ol-node${same(sel, s) ? ' on' : ''}${cls ? ` ${cls}` : ''}${payload ? ' draggable' : ''}`}
        style={{ paddingLeft: 10 + depth * 14 }}
        onClick={() => onSelect(s)}
        draggable={payload ? true : undefined}
        onDragStart={payload ? (e) => { e.dataTransfer.setData('text/plain', payload); e.dataTransfer.effectAllowed = 'copy'; onSelect(s); } : undefined}
      >
        <span className="fb-ol-glyph" aria-hidden="true">{glyph}</span>
        <span className="fb-ol-label">{label}</span>
        {extra}
      </button>
    );
  };

  function renderCont(sec: BSection, scope: string, grp: string, depth: number, nested: (marker: BField) => React.ReactNode): React.ReactNode[] {
    const items = inCont(fields, { sec: sec.id, scope, grp }, widgetSecIds);
    return items.map((f) => {
      if (f.slot) return null;
      if (f.cardId) {
        const card = sec.cards.find((c) => c.id === f.cardId);
        if (!card) return null;
        return (
          <React.Fragment key={f.key}>
            {node({ t: 'card', sec: sec.id, id: card.id }, depth, '▢', card.title || 'Card', tags('', cardTarget(sec.id, card.id)))}
            {renderCont(sec, f.scope, card.id, depth + 1, nested)}
          </React.Fragment>
        );
      }
      if (f.nodeScope) return <React.Fragment key={f.key}>{nested(f)}</React.Fragment>;
      const e = paletteEntryFor(f.type, f.scale);
      return (
        <React.Fragment key={f.key}>
          {node({ t: 'field', key: f.key }, depth, e?.glyph || '·', f.label || f.key, tags(f.key, f.key, f.key), 'field')}
        </React.Fragment>
      );
    });
  }

  function renderWNode(sec: BSection, n: WidgetNode, depth: number): React.ReactNode {
    return (
      <React.Fragment key={n.scope}>
        {node({ t: 'wnode', sec: sec.id, scope: n.scope }, depth, '⟳', `${n.itemLabel} (repeats)`, tags('', widgetTarget(n.scope)))}
        {renderCont(sec, n.scope, '', depth + 1, (marker) => {
          const child = (n.children || []).find((c) => c.scope === marker.nodeScope);
          return child ? renderWNode(sec, child, depth + 1) : null;
        })}
      </React.Fragment>
    );
  }

  function renderSection(sec: BSection, depth: number): React.ReactNode {
    const w = widgetFor(sec);
    const isWidget = sec.type === 'locations' || sec.type === 'widget';
    return (
      <React.Fragment key={sec.id}>
        {node({ t: 'sec', id: sec.id }, depth, '▭', sec.title || sec.id, tags('', sectionTarget(sec.id)))}
        {isWidget
          ? (w?.tree ? renderWNode(sec, w.tree, depth + 1) : null)
          : renderCont(sec, 'order', '', depth + 1, () => null)}
      </React.Fragment>
    );
  }

  const toggle = (id: string) => setShut((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="fb-outline" role="tree" aria-label="Form outline">
      <div className="fb-ol-head">
        <span className="fb-ol-title">Outline</span>
        <span className="fb-ol-count">{countLabel ?? `${nFields} field${nFields === 1 ? '' : 's'}`}</span>
      </div>
      {pages.map((p) => {
        const open = !shut.has(p.id);
        const secs = sections.slice(p.start, p.end);
        const label = `${p.n} · ${p.title || secs[0]?.title || 'Untitled page'}`;
        return (
          <div key={p.id} className="fb-ol-page">
            <div className="fb-ol-pagerow">
              <button type="button" className="fb-ol-twist" aria-label={open ? `Collapse page ${p.n}` : `Expand page ${p.n}`} aria-expanded={open}
                onClick={() => toggle(p.id)}>{open ? '▾' : '▸'}</button>
              <button type="button" role="treeitem" aria-selected={sel?.t === 'sec' && sel.id === p.id}
                className={`fb-ol-node page${sel?.t === 'sec' && sel.id === p.id ? ' on' : ''}`}
                onClick={() => onSelect({ t: 'sec', id: p.id })}>
                <span className="fb-ol-label">{label}</span>
              </button>
            </div>
            {open && secs.map((s) => renderSection(s, 1))}
          </div>
        );
      })}
      {!sections.length && <div className="fb-ol-empty">No sections yet.</div>}
      {(earlier || []).map((g, i) => {
        const id = `earlier:${i}`;
        const open = !shut.has(id);
        return (
          <div key={id} className="fb-ol-page">
            <div className="fb-ol-pagerow">
              <button type="button" className="fb-ol-twist" aria-label={open ? `Collapse ${g.label}` : `Expand ${g.label}`} aria-expanded={open}
                onClick={() => toggle(id)}>{open ? '▾' : '▸'}</button>
              {/* A heading, not a thing to place: it opens and shuts its answers. */}
              <button type="button" className="fb-ol-node page" title={g.label} onClick={() => toggle(id)}>
                <span className="fb-ol-label">{g.label}</span>
              </button>
            </div>
            {open && g.items.map((it) => (
              <React.Fragment key={it.key}>
                {node({ t: 'field', key: it.key }, 1, '↩', it.label, tags('', '', it.key), 'field')}
              </React.Fragment>
            ))}
          </div>
        );
      })}
      {(onAddField || onAddPage) && (
        <div className="fb-ol-foot">
          {onAddField && <button type="button" className="fb-ol-add" onClick={onAddField}>+ Add field <span className="fb-kbd">/</span></button>}
          {onAddPage && <button type="button" className="fb-ol-add" onClick={onAddPage}>+ Add page</button>}
        </div>
      )}
    </div>
  );
}
